import { useState } from 'react';

/**
 * What a box holding a list shows: the text as typed while it still reads as the list, and
 * the list written out once it does not.
 *
 * Writing the list out on every keystroke is what made the firm's numbers impossible to
 * type: the comma or space that starts the next one parses to nothing, is written back as
 * nothing, and is gone before the next digit. The list written out still wins whenever it
 * says something else — a save the server normalised, Discard, the page loading.
 */
export function listText<T>(
  typed: string,
  list: readonly T[],
  parse: (text: string) => T[],
  format: (list: readonly T[]) => string,
): string {
  const read = parse(typed);

  return read.length === list.length && read.every((value, index) => value === list[index])
    ? typed
    : format(list);
}

const commaSeparated = (list: readonly unknown[]) => list.join(', ');

/** The text for a list box, and the setter its `onChange` calls with what was typed. */
export function useListText<T>(
  list: readonly T[],
  parse: (text: string) => T[],
  format: (list: readonly T[]) => string = commaSeparated,
): [string, (typed: string) => void] {
  const [typed, setTyped] = useState(() => format(list));

  return [listText(typed, list, parse, format), setTyped];
}
